import {css, StyleSheet} from 'aphrodite';
import * as React from 'react';

import {assert} from 'Asserts';
import {
  BULK_ASSEMBLY_METHOD_TO_FRAGMENT_PRODUCTION_METHOD_TO_DISPLAY_PARAMS,
  BulkAssemblyMethod,
  DisplayFragmentProductionMethod,
  GibsonFragmentProductionMethod,
  GoldenGateFragmentProductionMethod,
} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {FONT_PROXIMA_11} from 'styles/FontConstants';
import FormGroup from 'ui-library/bs/FormGroup';
import {GRAY_DARK, GRAY_DARKER} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';

interface FragmentProductionMethodDropdownProps<
  FPM extends GoldenGateFragmentProductionMethod | GibsonFragmentProductionMethod,
> {
  bulkAssemblyMethod: BulkAssemblyMethod.GOLDEN_GATE | BulkAssemblyMethod.GIBSON;
  fragmentProductionMethod: DisplayFragmentProductionMethod;
  updateFragmentProductionMethod: (fragmentProductionMethod: FPM) => void;
  dropdownMethods: Array<FPM>;
  hideLabels?: boolean;
  disabled?: boolean;
}

/**
 * A dropdown to choose the fragment production method for a Golden Gate assembly.
 *
 * @param props Component props
 * @param {BulkAssemblyMethod} props.bulkAssemblyMethod The overall assembly method.
 * @param {DisplayFragmentProductionMethod} props.fragmentProductionMethod The fragment production method to show as selected in the dropdown.
 * @param {function} props.updateFragmentProductionMethod A function that takes a new fragment production method and uses it to update state.
 * @param {[FragmentProductionMethod]} props.methods The list of fragment production methods to show in the dropdown.
 * @param {boolean} props.hideLabels Whether to hide the 'Fragment production method' field label.
 * @param {boolean} props.disabled Whether to disable the dropdown.
 * @constructor
 */
export function FragmentProductionMethodDropdown<
  FPM extends GoldenGateFragmentProductionMethod | GibsonFragmentProductionMethod,
>(props: FragmentProductionMethodDropdownProps<FPM>): JSX.Element {
  const fragmentProductionMethodToDisplayParams =
    BULK_ASSEMBLY_METHOD_TO_FRAGMENT_PRODUCTION_METHOD_TO_DISPLAY_PARAMS[props.bulkAssemblyMethod];
  for (const dropdownMethod of [...props.dropdownMethods, props.fragmentProductionMethod]) {
    assert(
      fragmentProductionMethodToDisplayParams[dropdownMethod] != null,
      `Invalid fragment production method (${dropdownMethod}) for bulk assembly method (${props.bulkAssemblyMethod})`
    );
  }
  return (
    <FormGroup className={css(styles.noBottomMargin)}>
      {!props.hideLabels && (
        <FieldLabel label="Fragment production method" htmlFor="fragmentProductionMethod" />
      )}
      <SelectDropdown
        id="fragmentProductionMethod"
        displayName={fragmentProductionMethodToDisplayParams[props.fragmentProductionMethod]!.display}
        disabled={props.disabled}
        menu={
          <Menu value={props.fragmentProductionMethod} width={MenuWidth.FULL}>
            {props.dropdownMethods.map((method) => (
              <Option
                key={method}
                value={method}
                onSelect={() => {
                  props.updateFragmentProductionMethod(method);
                }}
                dataTestAttrs={{element: 'fragmentProductionMethod', key: method}}
              >
                <>
                  <div>{fragmentProductionMethodToDisplayParams[method]!.display}</div>
                  <div className={css(styles.detailText, styles.lightDetailText)}>
                    {fragmentProductionMethodToDisplayParams[method]!.details}
                  </div>
                </>
              </Option>
            ))}
          </Menu>
        }
        dataTestAttrs={{
          component: 'FragmentProductionMethodDropdown',
          element: 'selectDropdown',
        }}
      />
      {!props.hideLabels && !props.disabled && (
        <div className={css(styles.detailText, styles.darkDetailText)}>You can change this later.</div>
      )}
    </FormGroup>
  );
}

const styles = StyleSheet.create({
  detailText: {
    ...FONT_PROXIMA_11,
  },
  lightDetailText: {
    color: GRAY_DARK,
  },
  darkDetailText: {
    color: GRAY_DARKER,
  },
  noBottomMargin: {
    marginBottom: '0px',
  },
});
